import { create } from "zustand";
import { persist } from "zustand/middleware";
import {
	DEFAULT_BOTTOM_PANE_HEIGHT,
	DEFAULT_CENTER_RIGHT_SIZES,
	DEFAULT_LEFT_PANE_SIZES,
	DEFAULT_WORKSPACE_SIZES,
	LAYOUT_STATE_VERSION,
	migratePersistedLayoutState,
	rescaleLeftPaneSize,
} from "./layout-state";
import {
	clearSegmentationPaneShowState,
	readBottomPaneOpen,
	readBottomPaneTab,
	readSegmentationPaneShowState,
	writeBottomPaneOpen,
	writeBottomPaneTab,
	writeSegmentationPaneShowState,
	type BottomPaneTab,
	type RestorableBottomPaneTab,
} from "@/lib/bottom-pane-state";
import { fileTabIdsFromUrl } from "@/lib/file-tab-url";

/** サイドバーで切り替えるメイン領域の表示内容。 */
export type MainView = "chats" | "projects" | "volumes" | "billing" | "organization" | "devices";

/** URL パスのうちチャット以外のメインビューを表すもの。 */
export const MAIN_VIEW_PATHS: Record<Exclude<MainView, "chats">, string> = {
	projects: "/projects",
	volumes: "/volumes",
	billing: "/billing",
	organization: "/organization",
	devices: "/devices",
};

export function mainViewFromPathname(pathname: string): MainView {
	for (const [view, path] of Object.entries(MAIN_VIEW_PATHS)) {
		if (pathname.startsWith(path)) return view as MainView;
	}
	return "chats";
}

/** 画面下部のペインに表示する親タブ。segmentation は HITL のリクエストがある間だけ存在する。 */
export type { BottomPaneTab } from "@/lib/bottom-pane-state";

/** 表示中の request_segmentation。復元先のタブは手動状態 (manualBottomPaneTab) へ戻す。 */
export interface ActiveSegmentationPane {
	chatId: string;
	toolUseId: string;
	/** セグメンテーションタブを前面に出しているか。手動で別タブへ移ると false になる。 */
	showSegmentation: boolean;
	/** 終了後に下部ペインを開いた状態へ戻すか。開始前の値で凍結し、表示中の手動クローズだけが下げる。 */
	restoreOpen: boolean;
}

interface LayoutState {
	/** メイン領域の横幅 [left, center+right]。 */
	workspaceSizes: number[];
	/** workspaceSizes を保存したときの左右分割グループの実寸 (px)。ディスプレイを跨いだ復元に使う。 */
	workspaceGroupWidth: number | null;
	/** ワークスペース上段の横幅 [center, right]。 */
	centerRightSizes: number[];
	/** LeftPane の上下比率 [環境ファイル, チャット]。 */
	leftPaneSizes: number[];
	/** 下部ペインを最後に開いていたときの高さ。 */
	bottomPaneHeight: number;
	/** 下部ペインが展開中か。同じタブ内では再読み込み後も復元する。 */
	bottomPaneOpen: boolean;
	/** 下部ペインで表示中の親タブ。同じタブ内では再読み込み後も復元する。 */
	bottomPaneTab: BottomPaneTab;
	/** セグメンテーション終了時に戻す開閉状態。表示中は開始前の値で凍結する。 */
	manualBottomPaneOpen: boolean;
	/** 最後に手動で選んだ親タブ。セグメンテーション終了時はここへ戻す。 */
	manualBottomPaneTab: RestorableBottomPaneTab;
	/** このページ表示中にターミナルを一度でも表示したか。xterm の遅延生成に使い、永続化しない。 */
	terminalActivated: boolean;
	/** 表示中の request_segmentation に紐づく一時的な下部ペイン状態。永続化しない。 */
	segmentationPane: ActiveSegmentationPane | null;
	/** 左端サイドバーが折りたたまれているか (アイコンのみ表示)。 */
	sidebarCollapsed: boolean;
	/** 左ペイン (環境ファイル / チャット) が閉じているか。 */
	leftPaneCollapsed: boolean;
	/** 左ペイン上段 (環境ファイル) の見出しだけを残して折りたたんでいるか。 */
	environmentFilesCollapsed: boolean;
	/** 左ペイン下段 (チャット) の見出しだけを残して折りたたんでいるか。 */
	chatListCollapsed: boolean;
	/** メイン領域に表示中のビュー。localStorage には永続化せず URL と同期する (MainViewUrlSync)。 */
	mainView: MainView;
	/** 右ペインをピン留めし、開いているファイルがなくても自動で閉じないか。 */
	rightPanePinned: boolean;
	/** リロード直後に URL のファイルタブを復元し終えていないか。終わるまで右ペインを開いておく。永続化しない。 */
	fileTabRestorePending: boolean;
	/**
	 * 別のビューから新規チャットを始めたとき、チャットのビューが表示されたらコンポーザーへ
	 * フォーカスするための要求。チャットのビューは非表示の間もマウントされたままなので、
	 * 表示されるまでフォーカスを待つ必要がある。永続化しない。
	 */
	composerFocusRequested: boolean;
	/** groupWidth は sizes を測った時点の左右分割グループの実寸 (px)。未測定なら 0。 */
	setWorkspaceSizes: (sizes: number[], groupWidth: number) => void;
	setCenterRightSizes: (sizes: number[]) => void;
	setLeftPaneSizes: (sizes: number[]) => void;
	setBottomPaneHeight: (height: number) => void;
	setBottomPaneOpen: (open: boolean) => void;
	/** 親タブを選ぶ。下部ペインが折りたたまれていれば開く。 */
	openBottomPaneTab: (tab: BottomPaneTab) => void;
	/**
	 * 消えるタブ (セグメンテーション) から離れる。そのタブを表示中ならターミナルへ戻し、
	 * 表示していなければ何もしない。ペインの開閉状態は変えない。
	 */
	leaveBottomPaneTab: (tab: BottomPaneTab) => void;
	/** ショートカット用。そのタブを表示中なら折りたたみ、それ以外は開いて切り替える。 */
	toggleBottomPaneTab: (tab: BottomPaneTab) => void;
	/** request_segmentation の表示を開始する。同じ request への再実行は何もしない。 */
	startSegmentationPane: (chatId: string, toolUseId: string) => void;
	/** チャットから離れる。表示は最後の手動状態へ戻し、再表示の記録は残す。 */
	suspendSegmentationPane: () => void;
	/** request_segmentation が解決した。記録を捨て、最後の手動状態へ戻す。 */
	restoreSegmentationPane: () => void;
	/** 指定チャットの再表示記録だけを破棄する。表示中の下部ペインは変えない。 */
	discardSegmentationPaneShowState: (chatId: string) => void;
	toggleSidebar: () => void;
	setLeftPaneCollapsed: (collapsed: boolean) => void;
	toggleEnvironmentFiles: () => void;
	setChatListCollapsed: (collapsed: boolean) => void;
	toggleChatList: () => void;
	setMainView: (view: MainView) => void;
	finishFileTabRestore: () => void;
	toggleRightPanePinned: () => void;
	requestComposerFocus: () => void;
	clearComposerFocusRequest: () => void;
}

/** 下部ペインが開いていて、その親タブを表示中か。 */
function showsBottomPaneTab(state: LayoutState, tab: BottomPaneTab): boolean {
	return state.bottomPaneOpen && state.bottomPaneTab === tab;
}

function isRequestIdentity(value: string): boolean {
	return value.trim().length > 0 && value.length <= 512;
}

/** ターミナルが実際に見えているか (下部ペインが開き、ターミナルタブを表示中)。 */
export function selectTerminalVisible(state: LayoutState): boolean {
	return showsBottomPaneTab(state, "terminal");
}

/** 初期表示は URL から判定する (永続化はせず、ビューの復元は URL に委ねる)。 */
function initialMainView(): MainView {
	return mainViewFromPathname(window.location.pathname);
}

/** リサイズ中の連続更新で localStorage への同期書き込みが多発しないよう遅延させる猶予 (ミリ秒)。 */
const PERSIST_DEBOUNCE_MS = 200;

export const useLayoutStore = create<LayoutState>()(
	persist(
		(set, get) => {
			const initialBottomPaneOpen = readBottomPaneOpen();
			const initialBottomPaneTab = readBottomPaneTab();
			let workspaceSizesTimer: ReturnType<typeof setTimeout> | undefined;
			let centerRightSizesTimer: ReturnType<typeof setTimeout> | undefined;
			let leftPaneTimer: ReturnType<typeof setTimeout> | undefined;
			let bottomPaneHeightTimer: ReturnType<typeof setTimeout> | undefined;

			const setBottomPaneState = (patch: {
				bottomPaneOpen: boolean;
				bottomPaneTab: BottomPaneTab;
				segmentationPane: ActiveSegmentationPane | null;
				manualBottomPaneOpen?: boolean;
				manualBottomPaneTab?: RestorableBottomPaneTab;
			}) => {
				set((state) => ({
					...patch,
					terminalActivated:
						state.terminalActivated || (patch.bottomPaneOpen && patch.bottomPaneTab === "terminal"),
				}));
			};

			const rememberSegmentationPane = (pane: ActiveSegmentationPane) => {
				writeSegmentationPaneShowState(pane.chatId, {
					toolUseId: pane.toolUseId,
					showSegmentation: pane.showSegmentation,
					restoreOpen: pane.restoreOpen,
				});
			};

			/** 開閉とタブ選択をまとめて反映する。ターミナルは表示された時点で初めて生成する。 */
			const applyBottomPane = (bottomPaneOpen: boolean, bottomPaneTab: BottomPaneTab) => {
				const current = get();
				// Panel の onExpand/onCollapse は、同期処理で既に反映された値を通知することがある。
				// 同値更新を無視することで自動表示をユーザー操作として記録しない。
				if (current.bottomPaneOpen === bottomPaneOpen && current.bottomPaneTab === bottomPaneTab)
					return;
				// 見た目は手動操作をそのまま保存する。
				writeBottomPaneOpen(bottomPaneOpen);
				if (bottomPaneTab !== "segmentation") writeBottomPaneTab(bottomPaneTab);
				// 未解決のセグメンテーション中の「開く」操作は作業のための操作なので復元先へ昇格させない。
				// セグメンテーションタブを表示中かどうかは問わない (別タブへ移った後の操作も作業の一部)。
				// 「閉じる」操作だけは作業後の希望として扱う。
				const updatesManualOpen = current.segmentationPane === null || !bottomPaneOpen;
				const manual = {
					...(updatesManualOpen ? { manualBottomPaneOpen: bottomPaneOpen } : {}),
					// セグメンテーションは一時的なタブなので復元先のタブとしては記録しない。
					...(bottomPaneTab === "segmentation" ? {} : { manualBottomPaneTab: bottomPaneTab }),
				};
				const active = current.segmentationPane;
				const segmentationPane = active
					? {
							...active,
							showSegmentation: bottomPaneOpen && bottomPaneTab === "segmentation",
							...(updatesManualOpen ? { restoreOpen: bottomPaneOpen } : {}),
						}
					: null;
				if (segmentationPane) rememberSegmentationPane(segmentationPane);
				setBottomPaneState({ bottomPaneOpen, bottomPaneTab, segmentationPane, ...manual });
			};

			/** 自動表示・自動復帰。復元先 (手動状態) は更新しない。 */
			const applyAutomaticBottomPane = (
				bottomPaneOpen: boolean,
				bottomPaneTab: BottomPaneTab,
				segmentationPane: ActiveSegmentationPane | null,
			) => {
				const current = get();
				if (
					current.bottomPaneOpen === bottomPaneOpen &&
					current.bottomPaneTab === bottomPaneTab &&
					current.segmentationPane === segmentationPane
				)
					return;
				setBottomPaneState({ bottomPaneOpen, bottomPaneTab, segmentationPane });
			};

			/** 表示中のセグメンテーションを畳み、復元先の状態へ戻す。 */
			const showManualBottomPane = () => {
				const { manualBottomPaneOpen, manualBottomPaneTab } = get();
				// 表示中は見た目の開閉が復元先と乖離しうるため、保存値も復元先へ揃え直す。
				writeBottomPaneOpen(manualBottomPaneOpen);
				applyAutomaticBottomPane(manualBottomPaneOpen, manualBottomPaneTab, null);
			};
			return {
				workspaceSizes: [...DEFAULT_WORKSPACE_SIZES],
				workspaceGroupWidth: null,
				centerRightSizes: [...DEFAULT_CENTER_RIGHT_SIZES],
				leftPaneSizes: [...DEFAULT_LEFT_PANE_SIZES],
				bottomPaneHeight: DEFAULT_BOTTOM_PANE_HEIGHT,
				bottomPaneOpen: initialBottomPaneOpen,
				bottomPaneTab: initialBottomPaneTab,
				manualBottomPaneOpen: initialBottomPaneOpen,
				manualBottomPaneTab: initialBottomPaneTab,
				terminalActivated: initialBottomPaneOpen && initialBottomPaneTab === "terminal",
				segmentationPane: null,
				sidebarCollapsed: true,
				leftPaneCollapsed: false,
				environmentFilesCollapsed: false,
				chatListCollapsed: false,
				mainView: initialMainView(),
				setMainView: (mainView) => set({ mainView }),
				fileTabRestorePending:
					fileTabIdsFromUrl(new URLSearchParams(window.location.search)).length > 0,
				finishFileTabRestore: () => set({ fileTabRestorePending: false }),
				rightPanePinned: true,
				toggleRightPanePinned: () => set((state) => ({ rightPanePinned: !state.rightPanePinned })),
				composerFocusRequested: false,
				requestComposerFocus: () => set({ composerFocusRequested: true }),
				clearComposerFocusRequest: () => set({ composerFocusRequested: false }),
				setWorkspaceSizes: (workspaceSizes, groupWidth) => {
					clearTimeout(workspaceSizesTimer);
					// 左ペインを閉じた [0, ...] は保存しない。閉じる直前のドラッグ途中の幅も
					// 破棄することで、再び開いたときに閉じる前の幅へ戻る。
					if (workspaceSizes[0] <= 0) return;
					workspaceSizesTimer = setTimeout(
						() =>
							set(
								groupWidth > 0
									? { workspaceSizes, workspaceGroupWidth: groupWidth }
									: { workspaceSizes },
							),
						PERSIST_DEBOUNCE_MS,
					);
				},
				setCenterRightSizes: (centerRightSizes) => {
					clearTimeout(centerRightSizesTimer);
					// 右ペインを閉じた [..., 0] は保存しない (理由は setWorkspaceSizes と同じ)。
					if (centerRightSizes[1] <= 0) return;
					centerRightSizesTimer = setTimeout(() => set({ centerRightSizes }), PERSIST_DEBOUNCE_MS);
				},
				setLeftPaneSizes: (leftPaneSizes) => {
					clearTimeout(leftPaneTimer);
					leftPaneTimer = setTimeout(() => set({ leftPaneSizes }), PERSIST_DEBOUNCE_MS);
				},
				setBottomPaneHeight: (bottomPaneHeight) => {
					clearTimeout(bottomPaneHeightTimer);
					bottomPaneHeightTimer = setTimeout(() => set({ bottomPaneHeight }), PERSIST_DEBOUNCE_MS);
				},
				setBottomPaneOpen: (open) => applyBottomPane(open, get().bottomPaneTab),
				openBottomPaneTab: (tab) => applyBottomPane(true, tab),
				leaveBottomPaneTab: (tab) => {
					const { bottomPaneOpen, bottomPaneTab } = get();
					if (bottomPaneTab !== tab) return;
					applyBottomPane(bottomPaneOpen, "terminal");
				},
				toggleBottomPaneTab: (tab) => applyBottomPane(!showsBottomPaneTab(get(), tab), tab),
				startSegmentationPane: (chatId, toolUseId) => {
					if (!isRequestIdentity(chatId) || !isRequestIdentity(toolUseId)) return;
					const active = get().segmentationPane;
					if (active?.chatId === chatId && active.toolUseId === toolUseId) return;
					if (active) {
						// 同じチャットで別 request へ進んだときだけ旧記録を捨てる。別チャットの記録は
						// そのチャットへ戻ったときの再表示に使うため残す。
						if (active.chatId === chatId) clearSegmentationPaneShowState(chatId);
						showManualBottomPane();
					}
					const stored = readSegmentationPaneShowState(chatId);
					const restored = stored?.toolUseId === toolUseId ? stored : null;
					const showSegmentation = restored?.showSegmentation ?? true;
					const restoreOpen = restored?.restoreOpen ?? get().manualBottomPaneOpen;
					const pane: ActiveSegmentationPane = {
						chatId,
						toolUseId,
						showSegmentation,
						restoreOpen,
					};
					rememberSegmentationPane(pane);
					// 再読み込み直後の manualBottomPaneOpen は見た目由来なので、凍結値へ引き戻す。
					set({ manualBottomPaneOpen: restoreOpen });
					if (showSegmentation) applyAutomaticBottomPane(true, "segmentation", pane);
					else set({ segmentationPane: pane });
				},
				suspendSegmentationPane: () => {
					if (!get().segmentationPane) return;
					showManualBottomPane();
				},
				restoreSegmentationPane: () => {
					const active = get().segmentationPane;
					if (!active) return;
					clearSegmentationPaneShowState(active.chatId);
					showManualBottomPane();
				},
				discardSegmentationPaneShowState: (chatId) => clearSegmentationPaneShowState(chatId),
				toggleSidebar: () => set((state) => ({ sidebarCollapsed: !state.sidebarCollapsed })),
				setLeftPaneCollapsed: (leftPaneCollapsed) => set({ leftPaneCollapsed }),
				toggleEnvironmentFiles: () =>
					set((state) => ({ environmentFilesCollapsed: !state.environmentFilesCollapsed })),
				setChatListCollapsed: (chatListCollapsed) => set({ chatListCollapsed }),
				toggleChatList: () => set((state) => ({ chatListCollapsed: !state.chatListCollapsed })),
			};
		},
		{
			name: "izanagi-layout",
			version: LAYOUT_STATE_VERSION,
			migrate: migratePersistedLayoutState,
			partialize: (state) => ({
				workspaceSizes: state.workspaceSizes,
				workspaceGroupWidth: state.workspaceGroupWidth,
				centerRightSizes: state.centerRightSizes,
				leftPaneSizes: state.leftPaneSizes,
				bottomPaneHeight: state.bottomPaneHeight,
				sidebarCollapsed: state.sidebarCollapsed,
				leftPaneCollapsed: state.leftPaneCollapsed,
				environmentFilesCollapsed: state.environmentFilesCollapsed,
				chatListCollapsed: state.chatListCollapsed,
				rightPanePinned: state.rightPanePinned,
			}),
		},
	),
);

/**
 * 保存済みの左ペイン幅を、今のグループ幅で同じ実寸になる比率 (%) へ直す。
 * 記録が無い・保存時と同じ幅なら、保存済みの比率をそのまま使う。
 */
export function savedLeftPaneSize(groupWidth: number, minSize: number): number {
	const { workspaceSizes, workspaceGroupWidth } = useLayoutStore.getState();
	return (
		rescaleLeftPaneSize(workspaceSizes[0], workspaceGroupWidth, groupWidth, minSize) ??
		workspaceSizes[0]
	);
}
